/**
 * DemoModeBanner - indicates the organization is currently operating in demo mode.
 *
 * Rendered across all dashboard views for demo tenants so users know
 * records and transactions are simulated.
 */
import { useState } from 'react'
import { useAuthStore, useVerticalConfig } from '@lipaflow/shared'
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { isDemoTenant } from '../../lib/onboardingRouting'
import DemoGoLiveButton from './DemoGoLiveButton'

export default function DemoModeBanner() {
  const tenant = useAuthStore((s) => s.tenant)
  const { labels } = useVerticalConfig()
  const [showModal, setShowModal] = useState(false)

  if (!isDemoTenant(tenant)) return null

  const institutionLabel = labels.institution || 'Organization'

  return (
    <>
      <div
        role="status"
        aria-label="Demo mode banner"
        className="bg-teal-900 text-white px-4 py-2.5 flex items-center justify-between gap-4 shadow-sm"
      >
        <div className="flex items-center gap-2.5 font-inter text-body-sm min-w-0">
          <div className="p-1 rounded-md bg-teal-800 text-teal-200 flex-shrink-0">
            <SparklesIcon className="h-4 w-4" />
          </div>
          <p className="truncate">
            <span className="font-semibold text-teal-100">Demo Environment:</span>{' '}
            You are exploring {tenant?.name || `your ${institutionLabel.toLowerCase()}`} with sample
            data. No live payments or notifications will be processed.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="px-3 py-1 bg-white text-teal-900 hover:bg-teal-50 rounded font-medium text-caption transition-colors shadow-xs"
          >
            Go live
          </button>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/50 backdrop-blur-xs animate-fade-in"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-stone-200">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-50 rounded-xl text-teal-600">
                  <SparklesIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-outfit text-h3 text-stone-900">Go live</h3>
                  <p className="text-stone-500 font-inter text-body-sm">
                    Your own workspace, with your own data
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-stone-400 hover:text-stone-600 p-1 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6 text-stone-600 font-inter text-body-sm">
              <p>
                We create a clean workspace for {tenant?.name || `your ${institutionLabel.toLowerCase()}`}.
                You sign in with the same email and password; the sample data stays behind.
              </p>
              <div className="p-3 bg-stone-50 rounded-xl border border-stone-200/60 space-y-2">
                <div className="flex items-center gap-2 text-stone-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-600" />
                  <span>You have 30 days to finish setup; your 14-day free trial starts when you go live</span>
                </div>
                <div className="flex items-center gap-2 text-stone-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-600" />
                  <span>A few short setup steps: your details, phone sign-in and what your plan needs, such as the account payers pay into</span>
                </div>
                <div className="flex items-center gap-2 text-stone-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-600" />
                  <span>Where you collect payments, they switch on when the first one reaches your account</span>
                </div>
              </div>
            </div>

            <div className="flex items-start justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-stone-600 hover:text-stone-800 text-body-sm font-medium"
              >
                Keep exploring
              </button>
              <DemoGoLiveButton className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-body-sm font-medium transition-colors disabled:opacity-60" />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
